<template>
	<button
		v-if="iconToken"
		:aria-labelledby="`notification-count-label-${_uid}`"
		:class="[
			'notification-count notification-count--with-icon',
			colorClass
		]"
		@click="$emit('click')"
	>
		<helio-icon
			:color="iconColor"
			:token="iconToken"
			:weight="iconWeight"
		/>

		<span v-if="count > 0">
			<label
				v-if="type === 'number'"
				:id="`notification-count-label-${_uid}`"
				ref="count"
				:class="['notification-count__count', countPositionClass]"
			>
				<span class="visually-hidden">
					{{ formattedAriaLabel }}
				</span>

				{{ formattedCount }}
			</label>

			<span
				v-else
				:class="['notification-count__dot', colorClass]"
			>
				<span class="visually-hidden">
					{{ formattedAriaLabel }}
				</span>
			</span>
		</span>

		<span
			v-else
			class="visually-hidden"
		>
			{{ formattedAriaLabel }}
		</span>
	</button>

	<button
		v-else-if="!iconToken && count > 0"
		:class="[
			'notification-count notification-count--no-icon',
			{ 'notification-count--type-dot': type === 'dot' },
			colorClass,
		]"
		@click="$emit('click')"
	>
		<span class="visually-hidden">
			{{ formattedAriaLabel }}
		</span>

		<span v-if="type === 'number'">
			{{ formattedCount }}
		</span>
	</button>
</template>

<script>
import Icon from '../icon/Icon.vue';

export default {
	name: 'NotificationCount',

	components: {
		'helio-icon': Icon,
	},

	props: {
		ariaLabel: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the aria-label for the NotificationCount component',
		},

		color: {
			type: String,
			required: false,
			default: 'accent9-normal',
			description: 'Sets the background colour of the notification count dot. The colour provided must be one of those in the $colors-list.',
		},

		count: {
			type: Number,
			required: false,
			default: 0,
			description: 'Sets the number of unread notifications',
		},

		countLimit: {
			type: Number,
			required: false,
			default: 99,
			description: 'Sets the limit of visible notifications',
		},

		iconColor: {
			type: String,
			required: false,
			default: null,
			description: 'Sets NotificationCount icon color',
		},

		iconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets NotificationCount icon token',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'light',
			description: 'Sets NotificationCount icon weight',
		},

		type: {
			type: String,
			required: false,
			default: 'number',
			validator: value => ['number', 'dot'].indexOf(value) !== -1,
			description: 'Sets whether we want to show the number of notifications or not',
		},
	},

	computed: {
		colorClass() {
			return this.color ? `notification-count--color-${this.color}` : '';
		},

		countPositionClass() {
			return this.formattedCount.length === 1 ? 'notification-count__count--single-digit' : 'notification-count__count--multiple-digits';
		},

		formattedAriaLabel() {
			if (!this.ariaLabel) {
				if (this.count === 0) {
					return this.$t('notification-count.aria-label-empty');
				}

				return this.type === 'number' ? this.$t('notification-count.aria-label') : this.$t('notification-count.aria-label-dot');
			}

			return this.ariaLabel;
		},

		formattedCount() {
			return this.count > this.countLimit ? `${this.countLimit}+` : this.count.toString();
		},
	},
};
</script>

<style lang="scss" src="./notification-count.scss" />
